/* 3D 화면 보조. 장면 비우기, 카메라 맞춤, 캔버스 캡처와 빈 화면 판정 */
import { allObjects3d, resetRegistry3d } from './tools/executor3d';
import type { Math3DApp } from './tools/executor3d';

export interface CameraData { position: number[]; up: number[]; target: number[] }
export interface Math3DView extends Math3DApp {
  getCameraData: () => CameraData | undefined;
  setCameraData: (data: CameraData) => void;
}

/** 사용자 개체를 모두 지우고 등록부를 비운다. 지운 개체 수를 돌려준다 */
export async function clearScene3d(app: Math3DApp): Promise<number> {
  resetRegistry3d(app);
  const before = allObjects3d(app).length;
  for (const o of allObjects3d(app).slice().reverse()) {
    if (!allObjects3d(app).some((one) => one.id === o.id)) continue;
    try { app.commandManager.execute('object.deleteObject', { id: o.id }); } catch { /* 부모와 함께 지워진 개체 */ }
  }
  for (let i = 0; i < 20 && allObjects3d(app).length > 0; i += 1) await new Promise((resolve) => setTimeout(resolve, 50));
  return before;
}

const isVec = (v: unknown): v is number[] => Array.isArray(v) && v.length === 3 && v.every((n) => Number.isFinite(n));

/** 장면 전체가 들어오게 카메라를 맞춘다. 원점을 범위에 넣고(2D 와 같다) 방향은 view 가 있으면 그것을, 없으면 엔진 기본 방향을 쓴다. fill 은 화면에서 장면이 차지하는 비율 */
export function fitCamera3d(app: Math3DView, fill: number, view?: { toward: number[]; up: number[] }): void {
  const lo = [0, 0, 0]; const hi = [0, 0, 0];
  const expand = (p: number[], r = 0): void => { for (let i = 0; i < 3; i += 1) { lo[i] = Math.min(lo[i], p[i] - r); hi[i] = Math.max(hi[i], p[i] + r); } };
  for (const o of allObjects3d(app)) {
    if (o.isValid === false) continue;
    const r = Number.isFinite(o.radius) ? Number(o.radius) : 0;
    if (o.type === 'point' && isVec(o.position)) expand(o.position);
    else if ((o.type === 'sphere' || o.type === 'circular') && isVec(o.centerPointPosition)) expand(o.centerPointPosition, r);
    else if (o.type === 'cylindroconical') { if (isVec(o.bottomCenterPointPosition)) expand(o.bottomCenterPointPosition, r); if (isVec(o.topCenterPointPosition)) expand(o.topCenterPointPosition, r); }
  }
  const center = lo.map((v, i) => (v + hi[i]) / 2);
  const half = Math.max(Math.hypot(...hi.map((v, i) => (v - lo[i]) / 2)), 1e-6);
  const store = app.getStore();
  const camera = store.$camera;
  const base = store.getState().common.camera;
  const vertical = ((camera?.fov ?? 50) * Math.PI) / 360;
  const horizontal = Math.atan(Math.tan(vertical) * (camera?.aspect ?? 1));
  const distance = half / Math.sin(Math.min(vertical, horizontal)) / fill;
  const direction = view ? view.toward : base.direction;
  const length = Math.hypot(...direction) || 1;
  app.setCameraData({ position: center.map((c, i) => c + (direction[i] / length) * distance), up: view ? [...view.up] : [...base.up], target: center });
}

/** 캔버스를 흰 바탕 위에 합친 PNG(base64, 머리 없이). 흰색이 아닌 픽셀이 거의 없으면 빈 화면이다(WebGL 컨텍스트 손실 등) */
export function snapshotPng3d(): { png: string; blank: boolean } {
  const canvas = document.querySelector<HTMLCanvasElement>('.canvas canvas');
  if (!canvas || !canvas.width || !canvas.height) return { png: '', blank: true };
  const out = document.createElement('canvas');
  out.width = canvas.width; out.height = canvas.height;
  const g = out.getContext('2d');
  if (!g) return { png: '', blank: true };
  g.fillStyle = '#fff'; g.fillRect(0, 0, out.width, out.height);
  g.drawImage(canvas, 0, 0);
  const data = g.getImageData(0, 0, out.width, out.height).data;
  let ink = 0;
  for (let i = 0; i < data.length; i += 16) if (data[i] < 240 || data[i + 1] < 240 || data[i + 2] < 240) ink += 1;
  return { png: out.toDataURL('image/png').replace(/^data:image\/png;base64,/, ''), blank: ink < 50 };
}
